function getEndDateNotBeforeStart(startDate: Date, endDate: Date | string | null | undefined): Date {
  if (!endDate) return startDate;

  const parsedEnd = new Date(endDate);
  if (Number.isNaN(parsedEnd.getTime())) return startDate;

  return parsedEnd < startDate ? startDate : parsedEnd;
}

function getHoldEndDate(startDate: Date, days: number): Date {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + days);

  return endDate;
}

function getHoldEndDateFromDuration(newStartDate: Date, previousStartDate: Date, previousEndDate: Date): Date {
  const previousStartTime = new Date(previousStartDate).getTime();
  const previousEndTime = new Date(previousEndDate).getTime();
  const durationMs = previousEndTime - previousStartTime;
  const safeDurationMs = durationMs > 0 ? durationMs : 0;

  return new Date(newStartDate.getTime() + safeDurationMs);
}

// NOTE :: A typed hold follows its type's days, unless staff moved the end by hand — then the
// range they set is kept. A custom hold (holdDays null) always keeps its current range.
function getMovedHoldEndDate(
  newStartDate: Date,
  previousStartDate: Date | null,
  previousEndDate: Date | string | null | undefined,
  holdDays: number | null
): Date {
  const hasPreviousRange = !!previousStartDate && !!previousEndDate;
  if (!hasPreviousRange) {
    return holdDays === null
      ? getEndDateNotBeforeStart(newStartDate, previousEndDate)
      : getHoldEndDate(newStartDate, holdDays);
  }

  const previousEnd = new Date(previousEndDate);
  if (holdDays === null) return getHoldEndDateFromDuration(newStartDate, previousStartDate, previousEnd);

  const wasEndMovedByHand = previousEnd.getTime() !== getHoldEndDate(previousStartDate, holdDays).getTime();
  if (wasEndMovedByHand) return getHoldEndDateFromDuration(newStartDate, previousStartDate, previousEnd);

  return getHoldEndDate(newStartDate, holdDays);
}

function getHoldTypeDays(holdReason: { days?: number | string; hold_days?: number | string } | null | undefined) {
  const holdDays = Number(holdReason?.days ?? holdReason?.hold_days);
  if (!Number.isFinite(holdDays)) return null;

  return holdDays;
}

function getHoldLengthParts(fromDate: Date, toDate: Date) {
  const from = new Date(fromDate).getTime();
  const to = new Date(toDate).getTime();
  const diffTime = to - from;

  if (diffTime <= 0) {
    return { days: 0, hours: 0, minutes: 0 };
  }

  const totalMinutes = Math.floor(diffTime / (1000 * 60));
  const minutes = totalMinutes % 60;
  const totalHours = Math.floor(diffTime / (1000 * 60 * 60));
  const hours = totalHours % 24;
  const days = Math.floor(totalHours / 24);

  return { days, hours, minutes };
}

function formatHoldLength(fromDate: Date, toDate: Date) {
  const { days, hours, minutes } = getHoldLengthParts(fromDate, toDate);
  const daysString = days > 1 ? 'Days' : 'Day';
  const hoursString = hours > 1 ? 'Hours' : 'Hour';
  const minutesString = minutes > 1 ? 'Minutes' : 'Minute';

  return `${days} ${daysString} ${hours} ${hoursString} ${minutes} ${minutesString}`;
}

export { formatHoldLength, getHoldEndDate, getHoldTypeDays, getMovedHoldEndDate };
